import type React from "react"
import {
  BookOpen, Star, Calculator, Flame, Users, Lock,
  MapPin, Brain, Gamepad2, TrendingUp, ClipboardList,
  Trophy, Zap, Award, Target, Rocket, Globe, Palette,
  CheckCircle, BarChart2,
} from "lucide-react"

export interface Achievement {
  id: string
  name: string
  description: string
  icon: string
  isUnlocked: boolean
  unlockedDate?: string
  progress?: number
  rarity?: "common" | "rare" | "epic" | "legendary"
  points?: number
  badgeColor?: string
}

interface AchievementBadgeProps {
  achievement: Achievement
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  BookOpen, Star, Calculator, Flame, Users,
  MapPin, Brain, Gamepad2, TrendingUp, ClipboardList,
  Trophy, Zap, Award, Target, Rocket, Globe, Palette,
  CheckCircle, BarChart2,
}

const RARITY_RING: Record<string, string> = {
  common:    "border-slate-300",
  rare:      "border-indigo-400",
  epic:      "border-violet-500",
  legendary: "border-amber-400",
}

export default function AchievementBadge({ achievement }: AchievementBadgeProps) {
  const IconComponent = achievement.isUnlocked
    ? (iconMap[achievement.icon] ?? Star)
    : Lock

  const ringCls = achievement.isUnlocked
    ? `border-2 ${RARITY_RING[achievement.rarity ?? "common"] ?? "border-teal-400"}`
    : "border-2 border-slate-200"

  const hasPartialProgress =
    !achievement.isUnlocked &&
    typeof achievement.progress === "number" &&
    achievement.progress > 0

  return (
    <div
      className={`p-4 rounded-2xl text-center shadow-sm ${ringCls} transform hover:scale-105 transition-transform ${
        achievement.isUnlocked ? "bg-white" : "bg-slate-50 opacity-75"
      }`}
    >
      {/* Icon circle */}
      <div
        className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-3"
        style={achievement.isUnlocked && achievement.badgeColor
          ? { backgroundColor: `${achievement.badgeColor}20` }
          : { backgroundColor: "#e2e8f0" }}
      >
        <IconComponent
          className="w-8 h-8"
          style={achievement.isUnlocked && achievement.badgeColor
            ? { color: achievement.badgeColor }
            : { color: "#94a3b8" }}
        />
      </div>

      {/* Rarity chip */}
      {achievement.rarity && achievement.rarity !== "common" && (
        <span className={`inline-block text-[9px] font-bold px-1.5 py-0.5 rounded-full mb-1 uppercase tracking-wide ${
          achievement.rarity === "legendary" ? "bg-amber-100 text-amber-700" :
          achievement.rarity === "epic"      ? "bg-violet-100 text-violet-700" :
                                               "bg-indigo-100 text-indigo-700"
        }`}>
          {achievement.rarity}
        </span>
      )}

      <p className={`font-semibold text-sm leading-tight ${achievement.isUnlocked ? "text-slate-900" : "text-slate-500"}`}>
        {achievement.name}
      </p>
      <p className={`text-xs mt-0.5 leading-snug ${achievement.isUnlocked ? "text-slate-500" : "text-slate-400"}`}>
        {achievement.description}
      </p>

      {/* Points */}
      {achievement.points && achievement.isUnlocked && (
        <p className="text-[10px] font-bold mt-1.5" style={{ color: achievement.badgeColor ?? "#6366f1" }}>
          +{achievement.points} pts
        </p>
      )}

      {/* Progress bar for locked-but-started achievements */}
      {hasPartialProgress && (
        <div className="mt-2">
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-400 rounded-full transition-all duration-500"
              style={{ width: `${achievement.progress}%` }}
            />
          </div>
          <p className="text-[9px] text-slate-400 mt-0.5">{achievement.progress}%</p>
        </div>
      )}
    </div>
  )
}
